import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Stage01Consent } from './components/Stage01Consent';
import { Stage02IntakeFlow, IntakeData, EMPTY_INTAKE } from './components/Stage02IntakeFlow';
import { Stage03SelfieCapture, SelfieMeta } from './components/Stage03SelfieCapture';
import { StageScanPreview } from './components/StageScanPreview';
import { StageContact } from './components/StageContact';
import { StageSubmitting } from './components/StageSubmitting';
import { Stage05Success } from './components/Stage05Success';
import { FONT_STYLES } from './generated/fonts.css';
import vcTokensCss from './styles/vc-tokens.widget.css?inline';
import widgetComponentsCss from './styles/widget-components.css?inline';
import embedShellCss from './styles/embed-shell.css?inline';
import { EmbedShell } from './EmbedShell';
import { t, setWidgetLocale } from './i18n';
import { applyClinicBranding, loadClinicConfig, type ClinicConfig } from './lib/clinicConfig';

// Flow order (Option B — see SPECS/widget-contact-after-preview-scan.md):
// consent → profile/Q1–Q4 → photo → scan preview (visual-only) → contact →
// submit → success. The contact form now lands AFTER the scan preview; the real
// submission happens only at the Submitting stage.
enum WidgetStage {
  Consent = 1,
  IntakeForm = 2,
  SelfieCapture = 3,
  ScanPreview = 4,
  Contact = 5,
  Submitting = 6,
  Success = 7,
}

function buildAnswers(data: IntakeData): Array<{ question_key: string; answer: string }> {
  const out: Array<{ question_key: string; answer: string }> = [];
  if (data.age)       out.push({ question_key: 'age', answer: data.age });
  // NOTE: skin_type intentionally maps to question_key 'q5' for backward compatibility
  // with existing lead_answer data. Semantically misleading — q5 reads like a numbered
  // question but stores skin type. Must remain 'q5' to avoid breaking existing records.
  if (data.skin_type) out.push({ question_key: 'q5',  answer: data.skin_type });
  if (data.q1)        out.push({ question_key: 'q1',  answer: data.q1 });
  if (data.q2)        out.push({ question_key: 'q2',  answer: data.q2 });
  if (data.q3)        out.push({ question_key: 'q3',  answer: data.q3 });
  if (data.q4)        out.push({ question_key: 'q4',  answer: data.q4 });
  data.concerns.forEach((concern, i) => {
    out.push({ question_key: `concern_${i}`, answer: concern });
  });
  return out;
}

// Config-fetch beat — "the aperture warming up". Brand-neutral: warm cream
// strokes only, no clinic color before config lands.
const ApertureShell = ({ children }: { children?: React.ReactNode }) => (
  <div className="vc-frame">
    <div className="vc-aperture-center">
      <div className="vc-aperture">
        <i className="vc-corner ap tl"></i><i className="vc-corner ap tr"></i>
        <i className="vc-corner ap bl"></i><i className="vc-corner ap br"></i>
        <svg viewBox="0 0 200 240" aria-hidden="true">
          <ellipse
            cx="100" cy="120" rx="66" ry="92" fill="none"
            stroke="oklch(0.92 0.018 30 / 0.55)" strokeWidth="1.5"
            strokeLinecap="round" pathLength="100"
            strokeDasharray="58 42" strokeDashoffset="-8"
            className="vc-aperture-arc"
          ></ellipse>
          <ellipse
            cx="100" cy="120" rx="66" ry="92" fill="none"
            stroke="oklch(0.92 0.018 30 / 0.14)" strokeWidth="1"
            strokeDasharray="2 7" strokeLinecap="round"
          ></ellipse>
        </svg>
      </div>
      {children}
    </div>
  </div>
);

interface WidgetProps {
  clinicSlug: string;
  apiBase: string;
  shadowHost: HTMLElement;
}

export const Widget = ({ clinicSlug, apiBase, shadowHost }: WidgetProps) => {
  const [currentStage, setCurrentStage] = useState<WidgetStage>(WidgetStage.Consent);
  const [clinicConfig, setClinicConfig] = useState<ClinicConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [formData, setFormData] = useState<IntakeData | null>(null);
  const [intakeSubStep, setIntakeSubStep] = useState(1);
  const [selfiePhoto, setSelfiePhoto] = useState<File | null>(null);
  const [selfieMeta, setSelfieMeta] = useState<SelfieMeta | null>(null);
  const [photoTransform, setPhotoTransform] = useState<string | undefined>(undefined);
  const [consentAt, setConsentAt] = useState<string | null>(null);
  const [ariaLiveMessage, setAriaLiveMessage] = useState<string>('');

  // Tier-1 clinic theming: re-apply brand vars onto the shadow host once clinic
  // colours arrive (idempotent — EmbedShell already applied them at mount).
  // All chamber colors derive from these via color-mix in vc-tokens.widget.css.
  useEffect(() => {
    if (!clinicConfig) return;
    applyClinicBranding(shadowHost, clinicConfig);
  }, [clinicConfig, shadowHost]);

  // Resolve clinic configuration via the shared memoized loader. EmbedShell
  // prefetched it at embed mount (for FAB branding/locale), so this is usually
  // a cache hit; on a previous failure the cache was evicted and this retries.
  useEffect(() => {
    let cancelled = false;
    loadClinicConfig(apiBase, clinicSlug)
      .then((config) => {
        if (cancelled) return;
        // Set locale before state so the re-render reads localized copy via t().
        setWidgetLocale(config.locale);
        shadowHost.setAttribute('lang', config.locale);
        setClinicConfig(config);

        fetch(`${apiBase}/v1/public/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event: 'widget_start', clinic_slug: clinicSlug }),
        }).catch(() => { /* fire-and-forget — never block patient flow */ });
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[widget] clinic config failed', err);
        setConfigError(t('config.error'));
      });
    return () => {
      cancelled = true;
    };
  }, [clinicSlug, apiBase, shadowHost]);

  const advanceStage = (data?: any, meta?: SelfieMeta) => {
    if (currentStage === WidgetStage.Consent) {
      setConsentAt(new Date().toISOString());
      setIntakeSubStep(1);
      setFormData(null);
      setAriaLiveMessage(t('aria.toProfile'));
    } else if (currentStage === WidgetStage.IntakeForm) {
      setFormData(data);
      setAriaLiveMessage(t('aria.toPhoto'));

      fetch(`${apiBase}/v1/public/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: 'questions_completed', clinic_slug: clinicSlug }),
      }).catch(() => { /* fire-and-forget — never block patient flow */ });
    } else if (currentStage === WidgetStage.SelfieCapture) {
      setSelfiePhoto(data);
      setSelfieMeta(meta ?? null);
      setAriaLiveMessage(t('aria.toPreparing'));
    } else if (currentStage === WidgetStage.ScanPreview) {
      setAriaLiveMessage(t('aria.toContact'));
    } else if (currentStage === WidgetStage.Contact) {
      setFormData(data);
      setAriaLiveMessage(t('aria.toSubmitting'));
    } else if (currentStage === WidgetStage.Submitting) {
      setAriaLiveMessage(t('aria.submitted'));
    }
    setCurrentStage((prev) => prev + 1);
  };

  const goBack = () => {
    if (currentStage === WidgetStage.IntakeForm) {
      setAriaLiveMessage(t('aria.backToConsent'));
      setCurrentStage(WidgetStage.Consent);
    } else if (currentStage === WidgetStage.SelfieCapture) {
      setAriaLiveMessage(t('aria.backToDetails'));
      setCurrentStage(WidgetStage.IntakeForm);
    } else if (currentStage === WidgetStage.Contact) {
      // Skip back over the auto-playing scan preview to the photo step.
      setAriaLiveMessage(t('aria.backToPhoto'));
      setCurrentStage(WidgetStage.SelfieCapture);
    } else {
      setCurrentStage((prev) => prev - 1);
    }
  };

  const onCloseWidget = () => {
    setAriaLiveMessage(t('aria.closeSubmitted'));
  };

  if (configError) {
    return (
      <div className="widget-container">
        <ApertureShell>
          <div role="alert" className="vc-aperture-sub" style={{ padding: '0 var(--sp-8)', textAlign: 'center' }}>
            {configError}
          </div>
        </ApertureShell>
      </div>
    );
  }

  if (!clinicConfig) {
    return (
      <div className="widget-container">
        <div aria-busy="true">
          <ApertureShell>
          <div className="vc-aperture-label">{t('aperture.label')}</div>
          <div className="vc-aperture-sub">{t('aperture.sub')}</div>
          </ApertureShell>
        </div>
      </div>
    );
  }

  const renderStage = () => {
    switch (currentStage) {
      case WidgetStage.Consent:
        return <Stage01Consent clinicConfig={clinicConfig} onAdvance={() => advanceStage()} />;
      case WidgetStage.IntakeForm:
        return (
          <Stage02IntakeFlow
            clinicConfig={clinicConfig}
            initialSubStep={intakeSubStep}
            initialData={formData ?? EMPTY_INTAKE}
            onSubStepChange={setIntakeSubStep}
            onAdvance={advanceStage}
            onBack={goBack}
          />
        );
      case WidgetStage.SelfieCapture:
        return <Stage03SelfieCapture onAdvance={advanceStage} onBack={goBack} />;
      case WidgetStage.ScanPreview:
        return (
          <StageScanPreview
            selfiePhoto={selfiePhoto}
            selfieSource={selfieMeta?.source ?? null}
            guideRect={selfieMeta?.guideRect}
            onComplete={() => advanceStage()}
            onAligned={setPhotoTransform}
          />
        );
      case WidgetStage.Contact:
        return (
          <StageContact
            clinicConfig={clinicConfig}
            initialData={formData ?? EMPTY_INTAKE}
            onAdvance={(data) => advanceStage(data)}
            onBack={goBack}
          />
        );
      case WidgetStage.Submitting:
        return (
          <StageSubmitting
            formData={formData ?? EMPTY_INTAKE}
            answers={buildAnswers(formData ?? EMPTY_INTAKE)}
            selfiePhoto={selfiePhoto}
            photoTransform={photoTransform}
            consentAt={consentAt ?? new Date().toISOString()}
            clinicSlug={clinicSlug}
            apiBase={apiBase}
            onAdvance={() => advanceStage()}
            onRetryForm={(data) => {
              setFormData(data);
              setCurrentStage(WidgetStage.Contact);
              setAriaLiveMessage(t('aria.backToContact'));
            }}
          />
        );
      case WidgetStage.Success:
        return (
          <Stage05Success
            clinicConfig={clinicConfig}
            userEmail={formData?.email || ''}
            onCloseWidget={onCloseWidget}
          />
        );
      default:
        return <p>Unknown stage</p>;
    }
  };

  return (
    <div className="widget-container">
      <div aria-live="polite" aria-atomic="true" className="visually-hidden">
        {ariaLiveMessage}
      </div>

      {renderStage()}
    </div>
  );
};

export function mountWidget(element: HTMLElement, clinicSlug: string, apiBase: string) {
  const shadowRoot = element.attachShadow({ mode: 'open' });
  const styleTag = document.createElement('style');
  styleTag.textContent = FONT_STYLES + vcTokensCss + widgetComponentsCss + embedShellCss;
  shadowRoot.appendChild(styleTag);

  const widgetContainer = document.createElement('div');
  shadowRoot.appendChild(widgetContainer);

  const root = ReactDOM.createRoot(widgetContainer);
  root.render(
    <EmbedShell clinicSlug={clinicSlug} apiBase={apiBase} shadowHost={element} />,
  );

  return () => root.unmount();
}
